import { useMemo, useState, useEffect, ChangeEvent } from 'react';
import { Buffer } from 'buffer';
import FileSaver from 'file-saver';
import {
    Box,
    Button,
    Center,
    Checkbox,
    Flex,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Spinner,
    Text,
    Tooltip,
} from '@chakra-ui/react';
import {
    ColumnDef,
    SortingFn,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    useReactTable,
} from '@tanstack/react-table';
import { useGetRetailerByClientQuery } from 'core/API/authApi';
import { useGetCompanyInfoSelector } from 'core/redux/selectors/authSelectors';
import { SORTING_FNS } from 'plugins/DITable/helpers/sort';
import { useTranslation } from 'helpers/languages/translations';
import DefaultColumnFilter from 'plugins/DITable/filters/DefaultColumnFilter';
import { useGetSalesDataQuery, useLazyDownloadSalesDataQuery } from 'core/API/SalesDataApi';
import { TSalesData } from 'pages/SalesData/types';
import { ChevronDownIcon } from '@chakra-ui/icons';
import icons from 'shared/icons';

const useSalesData = () => {
    const [languages, setLanguages] = useState<string[]>([]);
    const [filters, setFilters] = useState<{ id: string; value: unknown }[]>([]);
    const { data: retailers, isLoading: isRetailersLoading, isError: isRetailerError } = useGetRetailerByClientQuery();
    const translate = useTranslation();
    const [selectedRows, setSelectedRows] = useState<string[]>([]);
    const { data: salesData, isError: isSalesDataError, isLoading: isSalesDataLoading } = useGetSalesDataQuery();
    const companyInfo = useGetCompanyInfoSelector();
    const [downloadFile] = useLazyDownloadSalesDataQuery();
    const [filesDownloading, setFilesDownloading] = useState<string[]>([]);

    useEffect(() => {
        if (!isRetailersLoading && !isRetailerError && retailers) {
            const langs: Set<string> = new Set<string>();
            retailers.data.forEach((retailer) => {
                if (retailer.languages) {
                    retailer.languages.forEach((l: string) => {
                        if (l !== '') langs.add(l);
                    });
                }
            });

            const langsArr = Array.from(langs);
            setLanguages(langsArr);
        }
    }, [retailers, isRetailersLoading]);

    const data: Readonly<TSalesData[]> = useMemo(() => {
        if (!isSalesDataLoading && !isSalesDataError && salesData) {
            return salesData;
        }
        return [] as TSalesData[];
    }, [isSalesDataLoading, isSalesDataError, salesData]);

    const caseInsensitiveSort = useMemo(() => SORTING_FNS.caseInsensitive, []);
    const sortTypes: Record<string, SortingFn<any>> = useMemo(
        () => ({
            ...SORTING_FNS,
        }),
        [],
    );

    const defaultColumn = useMemo(
        () => ({
            meta: {
                Filter: DefaultColumnFilter,
            },
        }),
        [],
    );

    const handleCheckAllRows = () => {
        if (salesData) {
            if (selectedRows.length < salesData.length) setSelectedRows(salesData.map((k) => k._id));
            else setSelectedRows([]);
        }
    };

    const handleCheckRow = (event: ChangeEvent<HTMLInputElement>, id: string) => {
        if (event.target.checked) setSelectedRows([...selectedRows, id]);
        else setSelectedRows(selectedRows.filter((e) => e !== id));
    };

    const columns: ColumnDef<TSalesData, any>[] = useMemo(() => {
        const nbLines = salesData ? salesData.length : 0;

        const tableColumns: (ColumnDef<TSalesData, any> | null)[] = [
            {
                header: () => (
                    <Center m={0}>
                        <Checkbox
                            onChange={handleCheckAllRows}
                            isIndeterminate={selectedRows.length > 0 && selectedRows.length < nbLines}
                            isChecked={nbLines > 0 && selectedRows.length === nbLines}
                        ></Checkbox>
                    </Center>
                ),
                id: 'selector',
                enableColumnFilter: false,
                cell: (info) => {
                    const cellValue = info.row.original as TSalesData;
                    return (
                        <Center>
                            <Checkbox
                                onChange={(e) => {
                                    handleCheckRow(e, cellValue._id);
                                }}
                                isChecked={selectedRows.includes(cellValue._id)}
                            ></Checkbox>
                        </Center>
                    );
                },
                meta: {
                    width: '5%',
                },
            } as ColumnDef<TSalesData, any>,

            {
                header: () => translate('sales_data.column.name') as string,
                accessorKey: 'name',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '10%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TSalesData;
                    return (
                        <Box maxW="300px">
                            <Text>{cellValue.name}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<TSalesData, any>,

            {
                header: () => translate('sales_data.column.comment') as string,
                accessorKey: 'comment',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '30%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TSalesData;
                    return (
                        <Box>
                            <Text>{cellValue.comment}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<TSalesData, any>,

            {
                header: () => translate('sales_data.column.size') as string,
                accessorKey: 'size',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '10%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TSalesData;
                    const fileSizeKb = (cellValue.size / 1024).toFixed(0);
                    const fileSizeMb = (cellValue.size / 1024 / 1024).toFixed(0);
                    const convertToMb = Number(fileSizeKb) > 1024;
                    return (
                        <Box display="flex">
                            <Text>
                                {convertToMb ? fileSizeMb : fileSizeKb} {convertToMb ? 'MB' : 'KB'}
                            </Text>
                        </Box>
                    );
                },
            } as ColumnDef<TSalesData, any>,

            {
                header: () => translate('sales_data.column.date') as string,
                accessorKey: 'date',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TSalesData;
                    const dateStr = cellValue.date;
                    const date = new Date(dateStr);
                    const formattedDate = date.toLocaleDateString('en-GB');
                    return (
                        <Box display="flex">
                            <Text>{formattedDate}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<TSalesData, any>,

            {
                header: () => translate('sales_data.column.user') as string,
                accessorKey: 'user',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TSalesData;
                    return (
                        <Box display="flex">
                            <Text>{cellValue.user}</Text>
                        </Box>
                    );
                },
            } as ColumnDef<TSalesData, any>,
            {
                header: () => translate('sales_data.column.status') as string,
                accessorKey: 'status',
                sortingFn: 'caseInsensitive',
                meta: {
                    width: '20%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TSalesData;
                    const tooltipProps: {
                        label?: string;
                    } = {};
                    if (cellValue.status === 'error') {
                        tooltipProps.label = cellValue.error_reason;
                    }

                    return (
                        <Tooltip {...tooltipProps}>
                            <Box display="flex" cursor="default">
                                <Text>{translate(`sales_data.status.${cellValue.status}`)}</Text>
                            </Box>
                        </Tooltip>
                    );
                },
            } as ColumnDef<TSalesData, any>,
            {
                header: () => translate(`sales_data.column.actions`) as string,
                accessorKey: 'actions',
                meta: {
                    width: '10%',
                },
                cell: (info) => {
                    const cellValue = info.row.original as TSalesData;
                    const DownloadIcon = icons.download;
                    const isDownloading = filesDownloading.includes(cellValue._id);
                    return (
                        <Flex>
                            <Menu size="xs">
                                <MenuButton
                                    size="xs"
                                    as={Button}
                                    rightIcon={isDownloading ? <Spinner size="sm" /> : <ChevronDownIcon />}
                                    isDisabled={isDownloading}
                                >
                                    {translate(`sales_data.select.actions`)}
                                </MenuButton>
                                <MenuList>
                                    <MenuItem
                                        onClick={async () => {
                                            setFilesDownloading((prev) => {
                                                if (!prev.includes(cellValue._id)) return [...prev, cellValue._id];
                                                return prev;
                                            });

                                            const res = await downloadFile({
                                                path: `${cellValue.company_id}/${cellValue.hash}`,
                                            });
                                            const binary = Buffer.from(res.data, 'binary');
                                            const blob = new Blob([binary]);

                                            FileSaver.saveAs(blob, cellValue.name);

                                            setFilesDownloading((prev) => {
                                                return prev.filter((id) => id !== cellValue._id);
                                            });
                                        }}
                                        icon={<DownloadIcon />}
                                    >
                                        {translate(`sales_data.action.download`)}
                                    </MenuItem>
                                </MenuList>
                            </Menu>
                        </Flex>
                    );
                },
            } as ColumnDef<TSalesData, any>,
        ];

        const filteredColumns = tableColumns.filter((e): e is ColumnDef<TSalesData, any> => e !== null);
        return filteredColumns;
    }, [salesData, languages, selectedRows, filesDownloading]);

    const table = useReactTable<TSalesData>({
        columns,
        data: data as TSalesData[],
        defaultColumn,
        sortingFns: sortTypes,
        initialState: { columnFilters: filters },
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    useEffect(() => {
        setFilters(table.getState().columnFilters);
    }, [table.getState().columnFilters]);

    const props = useMemo(
        () => ({
            languages,
            filters,
            retailers,
            isRetailersLoading,
            isRetailerError,
            isSalesDataLoading,
            columns,
            table,
            companyInfo,
            defaultColumn,
            data,
            sortTypes,
            setLanguages,
            caseInsensitiveSort,
            selectedRows,
        }),
        [
            languages,
            filters,
            retailers,
            isRetailersLoading,
            isRetailerError,
            isSalesDataLoading,
            isSalesDataError,
            columns,
            table,
            companyInfo,
            defaultColumn,
            data,
            sortTypes,
            selectedRows,
        ],
    );

    return props;
};

export type TColorsHooks = ReturnType<typeof useSalesData>;

export default useSalesData;
